import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { FileDown, Loader2, ShieldCheck, AlertCircle, CheckCircle2, ThumbsUp, BadgeCheck, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { LOGO_MARK } from "@/lib/brand";

const gbp = (n) => `£${(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => Number(v) || 0;

const PublicApplication = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [approving, setApproving] = useState(false);
  const [paying, setPaying] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/public/application/${token}`); setData(data); setState("ok"); }
    catch { setState("error"); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const cs = new URLSearchParams(window.location.search).get("cs");
    if (!cs) return;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data: res } = await api.get(`/public/application/${token}/payment-status/${cs}`);
        if (res.paid) {
          toast.success("Payment received — thank you!");
          window.history.replaceState({}, "", `/pay/${token}`);
          load();
          return;
        }
      } catch { /* keep trying */ }
      if (tries < 6) setTimeout(poll, 2000);
    };
    poll();
  }, [token, load]);

  const approve = async () => {
    setApproving(true);
    try { await api.post(`/public/application/${token}/approve`); toast.success("Application approved"); load(); }
    catch { toast.error("Could not approve"); }
    finally { setApproving(false); }
  };

  const payByCard = async () => {
    setPaying(true);
    try {
      const { data: res } = await api.post(`/public/application/${token}/pay`, { origin_url: window.location.origin });
      window.location.href = res.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start card payment. Please pay by bank transfer.");
      setPaying(false);
    }
  };

  if (state === "loading")
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  if (state === "error")
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-10 max-w-md text-center" data-testid="public-app-error">
          <AlertCircle className="h-10 w-10 text-amber-500 mx-auto" />
          <h1 className="font-display text-xl font-bold text-slate-900 mt-4">Link unavailable</h1>
          <p className="text-slate-500 mt-2 text-sm">This application for payment link is no longer available or has been revoked.</p>
        </div>
      </div>
    );

  const snap = data.snapshot;
  const proj = snap.project || {};
  const items = snap.line_items || [];
  const totals = snap.totals || {};
  const retain = num(snap.retainage_pct);
  const { approved, paid } = data;
  const pay = snap.payment || {};
  const hasBankDetails = !!(pay.account_number || pay.bank_name || pay.account_name);
  const showPayBar = data.allow_card && !paid;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="h-1.5 w-full" style={{ background: "linear-gradient(90deg,#16305C,#2563EB)" }} />
      <div className={`max-w-4xl mx-auto px-4 sm:px-6 pt-10 ${showPayBar ? "pb-32 sm:pb-10" : "pb-10"}`} data-testid="public-application">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div className="flex items-center gap-3">
            <img src={LOGO_MARK} alt="TradeOff" className="h-11 w-11 object-contain" />
            <div>
              <p className="font-display text-xl font-bold text-navy">TradeOff</p>
              <p className="text-xs text-slate-400 uppercase tracking-wider">Application for Payment</p>
            </div>
          </div>
          <Button onClick={() => window.open(`${API}/public/application/${token}/pdf`, "_blank")} data-testid="public-download-pdf" variant="outline" className="border-slate-300">
            <FileDown className="h-4 w-4 mr-1.5" /> Download PDF
          </Button>
        </div>

        {(approved || paid) && (
          <div className="flex flex-wrap gap-2 mb-4">
            {approved && <span className="inline-flex items-center gap-1.5 text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-1 rounded-full" data-testid="approved-badge"><BadgeCheck className="h-4 w-4" /> Approved</span>}
            {paid && <span className="inline-flex items-center gap-1.5 text-sm text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1 rounded-full" data-testid="paid-badge"><CheckCircle2 className="h-4 w-4" /> Paid</span>}
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl p-6 sm:p-8 shadow-sm">
          <div className="flex items-start justify-between flex-wrap gap-4 pb-6 border-b border-slate-100">
            <div>
              <h1 className="font-display text-2xl font-bold text-slate-900">{proj.name}</h1>
              <div className="mt-1 text-sm text-slate-500 space-y-0.5">
                {proj.client && <p>Client: {proj.client}</p>}
                {proj.address && <p>{proj.address}</p>}
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-400">Application dated</p>
              <p className="text-sm font-medium text-slate-700">{(snap.generated_at || "").slice(0, 10)}</p>
            </div>
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full ledger-table">
              <thead className="bg-slate-50">
                <tr className="text-xs uppercase tracking-wide text-slate-500 text-left">
                  <th>Description</th><th className="text-right">Scheduled</th><th className="text-right">Previous</th>
                  <th className="text-right">This period</th><th className="text-right">Stored</th><th className="text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-6">No line items.</td></tr>}
                {items.map((it, i) => {
                  const comp = num(it.previous_work) + num(it.this_period) + num(it.stored_materials);
                  return (
                    <tr key={it.id || `row-${i}`}>
                      <td className="text-slate-700">{it.description || "—"}</td>
                      <td className="text-right font-mono">{gbp(num(it.scheduled_value))}</td>
                      <td className="text-right font-mono">{gbp(num(it.previous_work))}</td>
                      <td className="text-right font-mono">{gbp(num(it.this_period))}</td>
                      <td className="text-right font-mono">{gbp(num(it.stored_materials))}</td>
                      <td className="text-right font-mono text-slate-500">{gbp(num(it.scheduled_value) - comp)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-6 items-start">
            <div className="text-sm text-slate-600 space-y-3">
              {snap.notes && (
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Notes</p>
                  <p className="whitespace-pre-line">{snap.notes}</p>
                </div>
              )}
              <p className="flex items-center gap-1.5 text-xs text-slate-400 pt-2"><ShieldCheck className="h-3.5 w-3.5" /> Prepared with TradeOff</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-5 space-y-2">
              <div className="flex justify-between text-sm"><span className="text-slate-500">Total completed & stored</span><span className="font-mono font-medium">{gbp(totals.completed)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">Less retainage ({retain}%)</span><span className="font-mono font-medium text-amber-600">- {gbp(totals.retainage_amount)}</span></div>
              <div className="flex justify-between text-sm border-t border-slate-200 pt-2"><span className="text-slate-500">Net after retention</span><span className="font-mono font-medium">{gbp(totals.net_after_retention)}</span></div>
              {totals.cis_rate > 0 && (
                <div className="flex justify-between text-sm" data-testid="public-cis-row"><span className="text-slate-500">Less CIS ({totals.cis_rate}% on labour)</span><span className="font-mono font-medium text-red-600">- {gbp(totals.cis_deduction)}</span></div>
              )}
              {totals.vat_treatment === "reverse_charge" ? (
                <div className="flex justify-between text-sm" data-testid="public-vat-row"><span className="text-slate-500">VAT — domestic reverse charge</span><span className="font-mono font-medium">{gbp(0)}</span></div>
              ) : totals.vat_treatment && !["exempt", "zero"].includes(totals.vat_treatment) ? (
                <div className="flex justify-between text-sm" data-testid="public-vat-row"><span className="text-slate-500">{totals.vat_label}</span><span className="font-mono font-medium">+ {gbp(totals.vat_amount)}</span></div>
              ) : null}
              <div className="flex justify-between items-center pt-3 mt-2 border-t border-slate-200">
                <span className="font-display font-bold text-navy">Amount due this period</span>
                <span className="font-mono text-xl font-bold text-blue-700" data-testid="public-amount-due">{gbp(totals.amount_due)}</span>
              </div>
              {totals.vat_treatment === "reverse_charge" && (
                <p className="text-xs text-slate-400 italic pt-1" data-testid="public-reverse-charge-note">VAT reverse charge applies. Customer to account for VAT to HMRC.</p>
              )}
            </div>
          </div>

          {hasBankDetails && !paid && (
            <div className="mt-6 rounded-lg border border-blue-100 bg-blue-50/60 p-5" data-testid="public-how-to-pay">
              <p className="font-display font-bold text-navy flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-blue-600" /> How to pay</p>
              <p className="text-sm text-slate-500 mt-0.5 mb-3">Please pay by bank transfer (BACS) using the details below.</p>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                {pay.account_name && <div className="flex justify-between"><span className="text-slate-500">Account name</span><span className="font-medium text-slate-800">{pay.account_name}</span></div>}
                {pay.bank_name && <div className="flex justify-between"><span className="text-slate-500">Bank</span><span className="font-medium text-slate-800">{pay.bank_name}</span></div>}
                {pay.sort_code && <div className="flex justify-between"><span className="text-slate-500">Sort code</span><span className="font-mono font-medium text-slate-800">{pay.sort_code}</span></div>}
                {pay.account_number && <div className="flex justify-between"><span className="text-slate-500">Account number</span><span className="font-mono font-medium text-slate-800">{pay.account_number}</span></div>}
                <div className="flex justify-between sm:col-span-2"><span className="text-slate-500">Payment reference</span><span className="font-medium text-slate-800">{pay.payment_reference || proj.name}</span></div>
              </div>
              {pay.payment_terms && <p className="text-xs text-slate-500 mt-3 italic">{pay.payment_terms}</p>}
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end border-t border-slate-100 pt-6">
            {!approved && !paid && (
              <Button variant="outline" onClick={approve} disabled={approving} data-testid="public-approve-btn" className="w-full sm:w-auto h-12 sm:h-10 text-base sm:text-sm border-green-300 text-green-700 hover:bg-green-50">
                {approving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ThumbsUp className="h-4 w-4 mr-1.5" />} Approve application
              </Button>
            )}
            {data.allow_card && !paid && (
              <Button onClick={payByCard} disabled={paying} data-testid="public-pay-card-btn" className="hidden sm:inline-flex bg-blue-600 hover:bg-blue-700">
                {paying ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CreditCard className="h-4 w-4 mr-1.5" />} Pay {gbp(totals.amount_due)} by card
              </Button>
            )}
            {paid && <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700" data-testid="public-paid-badge"><CheckCircle2 className="h-4 w-4" /> This application has been paid. Thank you!</span>}
          </div>
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">Shared securely by your contractor via TradeOff.</p>
      </div>

      {/* Sticky thumb-friendly pay bar (mobile) */}
      {showPayBar && (
        <div
          className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 px-4 pt-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)]"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.75rem)" }}
          data-testid="mobile-pay-bar"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-500">Amount due this period</span>
            <span className="font-mono text-lg font-bold text-blue-700">{gbp(totals.amount_due)}</span>
          </div>
          <Button onClick={payByCard} disabled={paying} data-testid="mobile-pay-card-btn" className="w-full h-14 text-base font-semibold bg-blue-600 hover:bg-blue-700">
            {paying ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CreditCard className="h-5 w-5 mr-2" />} Pay now
          </Button>
          <p className="text-center text-[11px] text-slate-400 mt-2 flex items-center justify-center gap-1"><ShieldCheck className="h-3 w-3" /> Secure card payment via Stripe</p>
        </div>
      )}
    </div>
  );
};

export default PublicApplication;
